import { useState } from "react";
import { ShieldCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";

export default function RecoveryPromptScreen() {
  const { completeRecovery, skipRecovery } = useAuth();
  const [passphrase, setPassphrase] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmingSkip, setConfirmingSkip] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await completeRecovery(passphrase);
      toast.success("Your messages are back");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "That didn't match");
      setPassphrase("");
    } finally {
      setLoading(false);
    }
  }

  async function handleSkip() {
    setLoading(true);
    try {
      await skipRecovery();
    } finally {
      setLoading(false);
    }
  }

  if (confirmingSkip) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background px-6 text-center">
        <ShieldCheck className="h-10 w-10 text-muted-foreground" />
        <div className="flex flex-col gap-2">
          <h1 className="text-xl font-bold">Are you sure?</h1>
          <p className="max-w-sm text-sm text-muted-foreground">
            Without the correct password or passphrase, your old conversations on this device can't be
            recovered. They'll stop showing up, and you'll need to message people again to start fresh
            with them. This can't be undone later.
          </p>
        </div>
        <div className="flex w-full max-w-xs flex-col gap-2">
          <Button variant="destructive" onClick={handleSkip} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue without recovering"}
          </Button>
          <Button variant="ghost" onClick={() => setConfirmingSkip(false)}>
            Go back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background px-6 text-center">
      <ShieldCheck className="h-14 w-14" />
      <div className="flex flex-col gap-2">
        <h1 className="text-xl font-bold">Welcome back</h1>
        <p className="max-w-sm text-sm text-muted-foreground">
          This looks like a new device. Enter your password (or your custom recovery passphrase, if
          you've set one) to restore access to your past conversations.
        </p>
      </div>
      <form onSubmit={submit} className="flex w-full max-w-xs flex-col gap-3">
        <Input
          autoFocus
          type="password"
          placeholder="Password or recovery passphrase"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
        />
        <Button type="submit" disabled={loading || !passphrase}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Restore my messages"}
        </Button>
        <button
          type="button"
          className="text-sm text-muted-foreground underline underline-offset-2"
          onClick={() => setConfirmingSkip(true)}
        >
          I don't have either
        </button>
      </form>
    </div>
  );
}
